import { unzip, unzipSync, type AsyncTerminable } from "fflate";
import { XMLParser, XMLValidator } from "fast-xml-parser";
import {
  Budget,
  TabularError,
  makeSheet,
  address,
  type Cell,
  type TabularResult,
} from "./types";
type Node = {
  tag: string;
  attrs: Record<string, string>;
  children: Node[];
  text: string;
};
const children = (n: Node | undefined, tag: string) =>
  n?.children.filter((c) => c.tag === tag) || [];
const child = (n: Node | undefined, tag: string) =>
  n?.children.find((c) => c.tag === tag);
const content = (n: Node | undefined): string =>
  n ? n.text + n.children.map(content).join("") : "";
const stringText = (n: Node | undefined): string =>
  n
    ? n.tag === "t"
      ? n.text
      : n.children
          .filter((c) => c.tag !== "rPh")
          .map(stringText)
          .join("")
    : "";
function xml(bytes: Uint8Array, budget: Budget): Node {
  let text: string;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    throw new TabularError("XML_ENCODING", "Workbook XML must be UTF-8");
  }
  if (
    /<!\s*(?:DOCTYPE|ENTITY)/i.test(text) ||
    text.includes("\0") ||
    /&#(?:0+|x0+);/i.test(text)
  )
    throw new TabularError(
      "UNSAFE_XML",
      "Workbook contains prohibited XML declarations or characters",
    );
  budget.check();
  if (XMLValidator.validate(text) !== true)
    throw new TabularError("INVALID_XML", "Workbook XML is malformed");
  const parsed = new XMLParser({
    preserveOrder: true,
    ignoreAttributes: false,
    attributeNamePrefix: "",
    parseTagValue: false,
    parseAttributeValue: false,
    trimValues: false,
    processEntities: true,
  }).parse(text) as Record<string, unknown>[];
  budget.check();
  let nodes = 0;
  const convert = (list: Record<string, unknown>[], depth = 0): Node[] => {
    if (depth > 64)
      throw new TabularError(
        "XML_DEPTH",
        "Workbook XML nesting exceeds its limit",
      );
    return list.flatMap((entry) => {
      budget.check();
      if (++nodes > Math.max(1000, budget.limits.cells * 12))
        throw new TabularError(
          "XML_NODES",
          "Workbook XML exceeds its node budget",
        );
      return Object.entries(entry)
        .filter(
          ([key]) =>
            key !== ":@" &&
            key !== "#text" &&
            !key.startsWith("?") &&
            !key.startsWith("!"),
        )
        .map(([key, val]) => {
          const parts = Array.isArray(val)
            ? (val as Record<string, unknown>[])
            : [];
          return {
            tag: key.replace(/^.*:/, ""),
            attrs: (entry[":@"] as Record<string, string>) || {},
            text: parts
              .filter((v) => "#text" in v)
              .map((v) => String(v["#text"]))
              .join(""),
            children: convert(parts, depth + 1),
          };
        });
    });
  };
  return { tag: "root", attrs: {}, text: "", children: convert(parsed) };
}
function safePart(name: string) {
  if (
    !name ||
    name.includes("\\") ||
    name.startsWith("/") ||
    /^[a-z]:/i.test(name) ||
    name.split("/").some((p) => p === ".." || p === ".") ||
    name.includes("\0")
  )
    throw new TabularError("ZIP_PATH", "Workbook archive has an unsafe path");
}
function allowed(name: string) {
  return /^(?:\[Content_Types\]\.xml|_rels\/\.rels|docProps\/(?:core|app|custom)\.xml|xl\/workbook\.xml|xl\/_rels\/workbook\.xml\.rels|xl\/(?:styles|sharedStrings)\.xml|xl\/theme\/theme\d+\.xml|xl\/worksheets\/sheet\d+\.xml|xl\/worksheets\/_rels\/sheet\d+\.xml\.rels|xl\/calcChain\.xml|xl\/metadata\.xml)$/i.test(
    name,
  );
}
async function archive(bytes: Uint8Array, budget: Budget) {
  let expanded = 0,
    count = 0;
  const names = new Set<string>();
  try {
    unzipSync(bytes, {
      filter: (e) => {
        budget.check();
        safePart(e.name);
        const key = e.name.toLowerCase();
        if (names.has(key))
          throw new TabularError(
            "ZIP_DUPLICATE",
            "Workbook archive has duplicate paths",
          );
        names.add(key);
        if (
          ++count > budget.limits.entries ||
          (expanded += e.originalSize) > budget.limits.expandedBytes ||
          e.originalSize > budget.limits.entryBytes ||
          e.originalSize / Math.max(1, e.size) > budget.limits.compressionRatio
        )
          throw new TabularError(
            "ZIP_LIMIT",
            "Workbook exceeds archive expansion limits",
          );
        if (
          /(?:vba|macro|activex|embeddings|externallinks|connections|querytables|customui)/i.test(
            e.name,
          )
        )
          throw new TabularError(
            "PROHIBITED_PART",
            "Workbook contains prohibited executable or connected content",
          );
        if (!e.name.endsWith("/") && !allowed(e.name))
          throw new TabularError(
            "UNSUPPORTED_PART",
            "Workbook contains an unsupported package part",
          );
        return false;
      },
    });
  } catch (error) {
    if (error instanceof TabularError) throw error;
    throw new TabularError(
      "INVALID_ZIP",
      "Workbook is not a valid supported ZIP package",
    );
  }
  return new Promise<Record<string, Uint8Array>>((resolve, reject) => {
    let terminate: AsyncTerminable = () => {},
      settled = false;
    const finish = (error: Error | null, data?: Record<string, Uint8Array>) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      budget.runtime.signal?.removeEventListener("abort", abort);
      if (error) {
        terminate();
        reject(
          error instanceof TabularError
            ? error
            : new TabularError(
                "INVALID_ZIP",
                "Workbook ZIP could not be decompressed",
              ),
        );
      } else resolve(data!);
    };
    const timer = setTimeout(
      () =>
        finish(
          new TabularError(
            "TIME_LIMIT",
            "Workbook decompression exceeded its time budget",
          ),
        ),
      Math.max(
        1,
        budget.limits.parseMs -
          ((budget.runtime.now || Date.now)() - budget.started),
      ),
    );
    const abort = () =>
      finish(new TabularError("CANCELLED", "Tabular import was cancelled"));
    budget.runtime.signal?.addEventListener("abort", abort, { once: true });
    if (budget.runtime.signal?.aborted) {
      abort();
      return;
    }
    try {
      terminate = unzip(
        bytes,
        { filter: (e) => !e.name.endsWith("/") },
        finish,
      );
    } catch (error) {
      finish(error as Error);
    }
  });
}
function relationTarget(base: string, target: string) {
  if (/[?#\\]/.test(target) || /^[a-z][a-z0-9+.-]*:/i.test(target))
    throw new TabularError(
      "EXTERNAL_RELATIONSHIP",
      "Workbook relation is not an internal package target",
    );
  let decoded: string;
  try {
    decoded = decodeURIComponent(target);
  } catch {
    throw new TabularError(
      "RELATIONSHIP",
      "Workbook relationship has an invalid target",
    );
  }
  const parts = (
      decoded.startsWith("/") ? decoded.slice(1) : base + decoded
    ).split("/"),
    out: string[] = [];
  for (const part of parts) {
    if (part === "..") {
      if (!out.length)
        throw new TabularError(
          "ZIP_PATH",
          "Relationship escapes the workbook package",
        );
      out.pop();
    } else if (part && part !== ".") out.push(part);
  }
  const result = out.join("/");
  safePart(result);
  return result;
}
function coords(ref: string) {
  const match = /^([A-Z]{1,3})([1-9]\d{0,6})$/.exec(ref);
  if (!match)
    throw new TabularError(
      "CELL_ADDRESS",
      "Workbook has an invalid cell address",
    );
  return {
    column: [...match[1]].reduce((n, c) => n * 26 + c.charCodeAt(0) - 64, 0),
    row: Number(match[2]),
  };
}
export async function parseWorkbook(
  bytes: Uint8Array,
  budget: Budget,
): Promise<TabularResult> {
  const files = await archive(bytes, budget);
  budget.check();
  const nodes = new Map<string, Node>();
  for (const [name, data] of Object.entries(files)) {
    budget.check();
    if (name.endsWith(".xml") || name.endsWith(".rels"))
      nodes.set(name, xml(data, budget));
  }
  const get = (name: string) => {
    const n = nodes.get(name);
    if (!n)
      throw new TabularError(
        "MISSING_PART",
        "Workbook is missing a required package part",
      );
    return n;
  };
  const types = get("[Content_Types].xml");
  if (
    /(?:macroenabled|macrosheet|vba|activex|oleobject|externalLink|connections)/i.test(
      content(types) + JSON.stringify(types),
    )
  )
    throw new TabularError(
      "PROHIBITED_PART",
      "Workbook content types include prohibited content",
    );
  for (const [name, node] of nodes) {
    if (!name.endsWith(".rels")) continue;
    for (const relation of children(
      child(node, "Relationships"),
      "Relationship",
    )) {
      if (
        String(relation.attrs.TargetMode).toLowerCase() === "external" &&
        !/\/hyperlink$/.test(relation.attrs.Type || "")
      )
        throw new TabularError(
          "EXTERNAL_RELATIONSHIP",
          "External workbook relationships are not allowed",
        );
      if (
        /(?:externalLink|connection|oleObject|vba|activeX)/i.test(
          relation.attrs.Type || "",
        )
      )
        throw new TabularError(
          "PROHIBITED_PART",
          "Workbook relationships include prohibited content",
        );
    }
  }
  const workbook = child(get("xl/workbook.xml"), "workbook")!,
    relationships = children(
      child(get("xl/_rels/workbook.xml.rels"), "Relationships")!,
      "Relationship",
    ),
    sheets = children(child(workbook, "sheets")!, "sheet");
  if (!sheets.length || sheets.length > budget.limits.sheets)
    throw new TabularError(
      "SHEET_LIMIT",
      "Workbook sheet count is outside its limits",
    );
  const sharedNode = nodes.get("xl/sharedStrings.xml"),
    strings = sharedNode
      ? children(child(sharedNode, "sst")!, "si").map((n) =>
          budget.text(stringText(n)),
        )
      : [];
  if (strings.length > budget.limits.cells)
    throw new TabularError(
      "SHARED_STRINGS_LIMIT",
      "Workbook has too many shared strings",
    );
  const styles = nodes.get("xl/styles.xml"),
    styleSheet = child(styles, "styleSheet"),
    customFormats = new Map(
      children(child(styleSheet, "numFmts"), "numFmt").map((n) => [
        Number(n.attrs.numFmtId),
        n.attrs.formatCode || "",
      ]),
    ),
    styleFormats = children(child(styleSheet, "cellXfs"), "xf").map((n) =>
      Number(n.attrs.numFmtId || 0),
    );
  const date1904 =
    child(workbook, "workbookPr")?.attrs.date1904 === "1" ||
    child(workbook, "workbookPr")?.attrs.date1904 === "true";
  const result = sheets.map((sheet, index) => {
    budget.check();
    const id = sheet.attrs["r:id"] || sheet.attrs.id,
      relation = relationships.find((r) => r.attrs.Id === id);
    if (!relation || !/\/worksheet$/.test(relation.attrs.Type || ""))
      throw new TabularError(
        "SHEET_RELATIONSHIP",
        "Workbook sheet relation is missing or unsupported",
      );
    const path = relationTarget("xl/", relation.attrs.Target || "");
    if (!/^xl\/worksheets\/sheet\d+\.xml$/.test(path))
      throw new TabularError(
        "SHEET_RELATIONSHIP",
        "Worksheet target is outside the supported package layout",
      );
    const worksheet = child(get(path), "worksheet")!,
      dimension = child(worksheet, "dimension")?.attrs.ref;
    if (dimension) {
      const parts = dimension.split(":"),
        first = coords(parts[0]),
        last = coords(parts.at(-1)!);
      if (
        first.row > last.row ||
        first.column > last.column ||
        last.column > budget.limits.columns ||
        last.row > 1048576
      )
        throw new TabularError(
          "DIMENSION_LIMIT",
          "Worksheet declared dimensions exceed their bounds",
        );
    }
    const records: { row: number; cells: Cell[] }[] = [],
      seen = new Set<string>();
    let previousRow = 0;
    for (const row of children(child(worksheet, "sheetData"), "row")) {
      budget.check();
      const rowNumber = Number(row.attrs.r || previousRow + 1);
      if (
        !Number.isInteger(rowNumber) ||
        rowNumber <= previousRow ||
        rowNumber > 1048576
      )
        throw new TabularError(
          "ROW_COORDINATE",
          "Worksheet rows are unordered or invalid",
        );
      previousRow = rowNumber;
      const cells: Cell[] = [];
      let lastColumn = 0;
      for (const cell of children(row, "c")) {
        const position = cell.attrs.r
          ? coords(cell.attrs.r)
          : { row: rowNumber, column: lastColumn + 1 };
        if (
          position.row !== rowNumber ||
          position.column <= lastColumn ||
          position.column > budget.limits.columns ||
          seen.has(address(position.row, position.column))
        )
          throw new TabularError(
            "CELL_ADDRESS",
            "Worksheet cells are duplicated, unordered or invalid",
          );
        lastColumn = position.column;
        seen.add(address(position.row, position.column));
        const type = cell.attrs.t || "n",
          v = content(child(cell, "v")),
          formula = child(cell, "f");
        let value = "";
        if (type === "s") {
          const i = Number(v);
          if (!/^\d+$/.test(v) || i >= strings.length)
            throw new TabularError(
              "SHARED_STRING",
              "Worksheet has an invalid shared-string reference",
            );
          value = strings[i];
        } else if (type === "inlineStr") value = stringText(child(cell, "is"));
        else if (type === "b") {
          if (v !== "0" && v !== "1")
            throw new TabularError(
              "CELL_VALUE",
              "Worksheet has an invalid boolean value",
            );
          value = v === "1" ? "true" : "false";
        } else if (type === "e") {
          budget.warn(
            "CELL_ERROR",
            "Spreadsheet error cell has no scalar value",
            {
              sheet: index,
              ...position,
              address: address(position.row, position.column),
            },
          );
          value = "";
        } else if (["n", "str", "d"].includes(type)) {
          value = v;
          const formatId = styleFormats[Number(cell.attrs.s || 0)] || 0,
            format = customFormats.get(formatId) || "",
            dateFormat =
              (formatId >= 14 && formatId <= 22) ||
              (formatId >= 45 && formatId <= 47) ||
              /[ymdhis]/i.test(format.replace(/"[^"]*"|\\.|\[[^\]]*\]/g, ""));
          if (type === "n" && v && dateFormat) {
            const serial = Number(v);
            if (!Number.isFinite(serial))
              throw new TabularError(
                "CELL_VALUE",
                "Worksheet date value is invalid",
              );
            const epoch = date1904
                ? Date.UTC(1904, 0, 1)
                : Date.UTC(1899, 11, serial < 60 ? 31 : 30),
              date = new Date(epoch + serial * 86400000);
            if (!Number.isFinite(date.getTime()))
              throw new TabularError(
                "CELL_VALUE",
                "Worksheet date is outside its supported range",
              );
            value =
              serial % 1 ? date.toISOString() : date.toISOString().slice(0, 10);
          }
        } else
          throw new TabularError(
            "CELL_TYPE",
            "Worksheet has an unsupported cell type",
          );
        const parsed = budget.cell(value, index, position.row, position.column);
        cells[position.column - 1] = parsed;
        if (formula) {
          budget.text(content(formula));
        }
        if (formula)
          budget.warn(
            "FORMULA_NOT_EXECUTED",
            "Formula was not executed; only its cached value was read",
            parsed.location,
          );
      }
      if (cells.some(Boolean)) records.push({ row: rowNumber, cells });
      if (
        records.length >
        budget.limits.rows +
          1 +
          (typeof budget.options.headerRow === "number"
            ? budget.options.headerRow
            : 0)
      )
        throw new TabularError("ROW_LIMIT", "Worksheet exceeds its row budget");
    }
    for (const link of children(child(worksheet, "hyperlinks"), "hyperlink")) {
      let location;
      try {
        const point = coords((link.attrs.ref || "").split(":")[0]);
        location = {
          sheet: index,
          ...point,
          address: address(point.row, point.column),
        };
      } catch {}
      budget.warn("LINK_IGNORED", "Hyperlink metadata was ignored", location);
    }
    return makeSheet(
      sheet.attrs.name || `Sheet ${index + 1}`,
      index,
      records,
      budget,
    );
  });
  budget.check();
  return {
    version: 1,
    format: "xlsx",
    encoding: "xlsx",
    delimiter: "",
    sheets: result,
    diagnostics: budget.diagnostics,
  };
}
